import { api } from '../../../utils/client';
import type {
  AdminUser,
  BulkActionResponse,
  BulkCreateResponse,
  UserFormValue,
  UserListResponse,
  UsersMeta,
} from './types';
import { formToPayload } from './types';

export async function fetchUsersMeta() {
  return api<UsersMeta>('/api/admin/users/meta/');
}

export interface UserListParams {
  q?: string;
  org?: number | '';
  user_type?: string | number | '';
  is_active?: '' | 'true' | 'false';
  page?: number;
  page_size?: number;
}

export async function fetchUsers(params: UserListParams) {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.org) search.set('org', String(params.org));
  if (params.user_type) search.set('user_type', String(params.user_type));
  if (params.is_active) search.set('is_active', params.is_active);
  search.set('page', String(params.page || 1));
  search.set('page_size', String(params.page_size || 50));
  return api<UserListResponse>(`/api/admin/users/?${search.toString()}`);
}

export async function createUser(value: UserFormValue) {
  return api<AdminUser>('/api/admin/users/', {
    method: 'POST',
    body: JSON.stringify(formToPayload(value)),
  });
}

export async function updateUser(id: number, value: UserFormValue) {
  return api<AdminUser>(`/api/admin/users/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(formToPayload(value)),
  });
}

export async function setUserActive(id: number, isActive: boolean) {
  return api<AdminUser>(`/api/admin/users/${id}/set-active/`, {
    method: 'POST',
    body: JSON.stringify({ is_active: isActive }),
  });
}

export async function downloadUserTemplate() {
  const res = await fetch('/api/admin/users/bulk-template/', {
    credentials: 'include',
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      message = body?.detail || body?.error || message;
    } catch {
      /* ignore */
    }
    throw new Error(message || 'Failed to download template');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'user_import_template.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function bulkCreateUsers(csv: string, dryRun: boolean) {
  return api<BulkCreateResponse>('/api/admin/users/bulk-create/', {
    method: 'POST',
    body: JSON.stringify({ csv, dry_run: dryRun }),
  });
}

export async function bulkUserAction(payload: Record<string, unknown>) {
  return api<BulkActionResponse>('/api/admin/users/bulk-action/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
